export type Severity = "low" | "medium" | "high" | "critical";
export type EntityType = "person" | "email" | "domain" | "ip" | "wallet" | "org" | "username" | "phone" | "device" | "location";

export interface Entity {
  id: string;
  type: EntityType;
  label: string;
  sub?: string;
  confidence: number; // 0-100
  risk: number; // 0-100
}

export interface Edge { from: string; to: string; kind: string; weight: number; }

export interface Finding {
  id: string;
  source: string;
  category: string;
  severity: Severity;
  title: string;
  description: string;
  ts: string;
  confidence: number;
  /** Provenance chain — carried from the collector that produced the evidence. */
  url?: string;
  sourceId?: string;
  lane?: string;
  retrievedAt?: string;
  latencyMs?: number;
  reliability?: number;
  /** Supporting hosts/probes that independently produced the same fact. */
  corroboration?: number;
}

export type TimelineCategory =
  | "domain"
  | "certificate"
  | "corporate"
  | "legal"
  | "document"
  | "social"
  | "breach"
  | "blockchain"
  | "news"
  | "archive"
  | "threat"
  | "run";

export interface TimelineEvent {
  id: string;
  ts: string;
  title: string;
  source: string;
  severity: Severity;
  /** Evidence class used for grouping/filtering. Missing on legacy records. */
  category?: TimelineCategory;
  /** How precise the underlying date is. */
  precision?: "day" | "month" | "year" | "approx";
  /** "evidence" = reconstructed from collected data, "run" = scan lifecycle. */
  kind?: "evidence" | "run";
  /** Link to the underlying artefact when the source exposed one. */
  ref?: string;
}

export const TIMELINE_CATEGORY_LABEL: Record<TimelineCategory, string> = {
  domain: "Domain",
  certificate: "Certificates",
  corporate: "Company",
  legal: "Court / legal",
  document: "Documents",
  social: "Social / accounts",
  breach: "Leaked records",
  blockchain: "Blockchain",
  news: "News",
  archive: "Archive",
  threat: "Threat intel",
  run: "Run activity",
};

// All canned subjects/findings/timeline removed — only real, user-triggered
// scan results should appear in the UI. Types remain for the scan pipeline.
export const ENTITIES: Entity[] = [];
export const EDGES: Edge[] = [];
export const FINDINGS: Finding[] = [];
export const TIMELINE: TimelineEvent[] = [];
export const SOURCES: string[] = [];

export const SEVERITY_COLOR: Record<Severity, string> = {
  low: "var(--success)",
  medium: "var(--amber)",
  high: "oklch(0.72 0.20 50)",
  critical: "var(--danger)",
};
